/**
 * Hook for browsing and previewing voices from TTS providers.
 * Uses React Query for data fetching with stale-while-revalidate caching.
 */

import { useCallback, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'
import type {
  BrowsableVoice,
  VoiceBrowserResponse,
  VoiceSearchFilters,
  ProviderCapabilities,
} from '@/components/voice-browser/types'

const STALE_TIME = 5 * 60 * 1000 // 5 minutes — matches backend cache TTL

async function fetchCapabilities(provider: string): Promise<ProviderCapabilities> {
  const resp = await apiClient.fetch(`/api/voice-browser/${provider}/capabilities`)
  if (!resp.ok) throw new Error(`Failed to fetch capabilities: ${resp.statusText}`)
  return resp.json()
}

async function fetchUserVoices(provider: string, search?: string): Promise<VoiceBrowserResponse> {
  const params = new URLSearchParams()
  if (search) params.set('search', search)
  const qs = params.toString()
  const resp = await apiClient.fetch(`/api/voice-browser/${provider}/voices${qs ? `?${qs}` : ''}`)
  if (!resp.ok) throw new Error(`Failed to fetch voices: ${resp.statusText}`)
  return resp.json()
}

async function fetchLibraryVoices(
  provider: string,
  filters: VoiceSearchFilters
): Promise<VoiceBrowserResponse> {
  const params = new URLSearchParams()
  if (filters.search) params.set('search', filters.search)
  if (filters.gender) params.set('gender', filters.gender)
  if (filters.language) params.set('language', filters.language)
  if (filters.age) params.set('age', filters.age)
  if (filters.accent) params.set('accent', filters.accent)
  if (filters.page_size) params.set('page_size', String(filters.page_size))
  if (filters.page) params.set('page', String(filters.page))
  const qs = params.toString()
  const resp = await apiClient.fetch(`/api/voice-browser/${provider}/library${qs ? `?${qs}` : ''}`)
  if (!resp.ok) throw new Error(`Failed to fetch library: ${resp.statusText}`)
  return resp.json()
}

export function useVoiceBrowser(provider: string, enabled: boolean = true) {
  const [userSearch, setUserSearch] = useState('')
  const [filters, setFilters] = useState<VoiceSearchFilters>({})
  const [libraryPage, setLibraryPage] = useState(0)
  const [accumulatedLibraryVoices, setAccumulatedLibraryVoices] = useState<BrowsableVoice[]>([])
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null)
  const [isSynthesizing, setIsSynthesizing] = useState(false)
  const synthAudioRef = useRef<HTMLAudioElement | null>(null)

  // Provider capabilities
  const capabilitiesQuery = useQuery({
    queryKey: ['voice-browser', provider, 'capabilities'],
    queryFn: () => fetchCapabilities(provider),
    staleTime: Infinity,
    enabled,
  })

  // User voices
  const userVoicesQuery = useQuery({
    queryKey: ['voice-browser', provider, 'user-voices', userSearch],
    queryFn: () => fetchUserVoices(provider, userSearch || undefined),
    staleTime: STALE_TIME,
    enabled,
  })

  // Library voices
  const currentFilters = { ...filters, page: libraryPage, page_size: 30 }
  const libraryQuery = useQuery({
    queryKey: ['voice-browser', provider, 'library', currentFilters],
    queryFn: () => fetchLibraryVoices(provider, currentFilters),
    staleTime: STALE_TIME,
    enabled: enabled && (capabilitiesQuery.data?.supports_library ?? false),
  })

  // When library data changes, accumulate for "load more"
  const lastPageRef = useRef(-1)
  if (libraryQuery.data && libraryPage !== lastPageRef.current) {
    lastPageRef.current = libraryPage
    if (libraryPage === 0) {
      setAccumulatedLibraryVoices(libraryQuery.data.voices)
    } else {
      setAccumulatedLibraryVoices((prev) => [...prev, ...libraryQuery.data!.voices])
    }
  }

  const loadMore = useCallback(() => {
    if (libraryQuery.data?.has_more) {
      setLibraryPage((p) => p + 1)
    }
  }, [libraryQuery.data?.has_more])

  const updateFilters = useCallback((newFilters: Partial<VoiceSearchFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }))
    setLibraryPage(0)
    setAccumulatedLibraryVoices([])
    lastPageRef.current = -1
  }, [])

  const clearFilters = useCallback(() => {
    setFilters({})
    setLibraryPage(0)
    setAccumulatedLibraryVoices([])
    lastPageRef.current = -1
  }, [])

  // Audio preview — pre-recorded samples
  const playPreview = useCallback((voice: BrowsableVoice) => {
    if (!voice.preview_url) return

    // Stop current playback
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }

    if (playingVoiceId === voice.voice_id) {
      setPlayingVoiceId(null)
      return
    }

    const audio = new Audio(voice.preview_url)
    audioRef.current = audio
    setPlayingVoiceId(voice.voice_id)

    audio.addEventListener('ended', () => {
      setPlayingVoiceId(null)
      audioRef.current = null
    })
    audio.addEventListener('error', () => {
      setPlayingVoiceId(null)
      audioRef.current = null
    })
    audio.play()
  }, [playingVoiceId])

  const stopPreview = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }
    setPlayingVoiceId(null)
  }, [])

  // Custom text synthesis
  const synthesizeCustom = useCallback(async (voiceId: string, text: string) => {
    setIsSynthesizing(true)
    try {
      // Stop any current playback
      if (synthAudioRef.current) {
        synthAudioRef.current.pause()
        synthAudioRef.current = null
      }

      const resp = await apiClient.fetch(`/api/voice-browser/${provider}/preview`, {
        method: 'POST',
        body: JSON.stringify({ voice_id: voiceId, text }),
      })
      if (!resp.ok) throw new Error('Preview synthesis failed')

      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const audio = new Audio(url)
      synthAudioRef.current = audio

      audio.addEventListener('ended', () => {
        URL.revokeObjectURL(url)
        synthAudioRef.current = null
      })
      await audio.play()
    } finally {
      setIsSynthesizing(false)
    }
  }, [provider])

  return {
    // Capabilities
    capabilities: capabilitiesQuery.data ?? null,
    capabilitiesLoading: capabilitiesQuery.isLoading,

    // User voices
    userVoices: userVoicesQuery.data?.voices ?? [],
    userVoicesLoading: userVoicesQuery.isLoading,
    userVoicesError: userVoicesQuery.error,
    userSearch,
    setUserSearch,

    // Library
    libraryVoices: accumulatedLibraryVoices,
    libraryLoading: libraryQuery.isLoading,
    libraryError: libraryQuery.error,
    hasMore: libraryQuery.data?.has_more ?? false,
    loadMore,
    totalCount: libraryQuery.data?.total_count ?? null,

    // Filters
    filters,
    setFilters: updateFilters,
    clearFilters,

    // Preview
    playingVoiceId,
    playPreview,
    stopPreview,
    synthesizeCustom,
    isSynthesizing,
  }
}
